/* ORIO Home — warm, premium PWA daily starting point.
   Matches the mockup #1:
     - Top greeting "Ciao [name]! 👋"
     - Weather chip / safety status
     - 3 stat tiles (Missioni attive · Cani aiutati · Rifugi connessi)
     - 'Allerta caldo' warm card (when HeatWarning matches)
     - Mini map "In zona"
     - Lista ultime segnalazioni
     - Banner ORIO PRO premium light/cream
   All API calls and data flow PRESERVED.
*/
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Map, HeartHandshake, Bell, Crown, Footprints, AlertTriangle,
  Home as HomeIcon, PawPrint, ChevronRight, Sparkles, Sun, Heart, Briefcase, ArrowRight, BookOpen,
  Stethoscope, MapPin, Shield, TreePine, Camera, QrCode, FileText, Activity, Users, Snowflake, Wind, Zap,
  Siren, AlertOctagon, Archive, MessageSquareHeart, GraduationCap, Building2, Compass,
  Play, Flame, Lock,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import HeatWarning from "@/components/HeatWarning";
import DailyReport from "@/components/DailyReport";
import QuickHazardCapture from "@/components/QuickHazardCapture";
import TipsSheet from "@/components/TipsSheet";
import WeeklyQuests from "@/components/WeeklyQuests";
import WeekendBoost from "@/components/WeekendBoost";
import CityLeaderboard from "@/components/CityLeaderboard";
import PlanComparison from "@/components/PlanComparison";
import HomeSection from "@/components/HomeSection";
import WelcomeHero from "@/components/WelcomeHero";
// Note: WelcomeHero + DailyReport are no longer rendered (replaced by the
// ORIO AI hero + rotating contextual placeholders). Imports kept for now to
// avoid breaking other code paths that might still reference the component.
import HomeStoriesStrip from "@/components/HomeStoriesStrip";
import HomeStoriesRibbon from "@/components/HomeStoriesRibbon";
import HomeFeatureGallery from "@/components/HomeFeatureGallery";
import HomeLiveStrip from "@/components/HomeLiveStrip";
import OrioAIHero from "@/components/OrioAIHero";
import { Section, List, Row, StatTile, Carousel } from "@/components/orio";
import { hapticTap } from "@/lib/haptics";
import { orioIllustration, orioIllustrationFallback } from "@/lib/illustrations";
import IlloImg from "@/components/IlloImg";
import { useOrioRefresh, ORIO_EVENTS } from "@/lib/events";

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isPro, canStartTrial, startTrial } = useSubscription();

  // Ref to the OrioAIHero wrapper — used with scroll listener to hide
  // the mobile top-bar + bottom-nav while the AI block frame is in view.
  // Simple threshold: while page scrollY < 120px, we're on the hero.
  const heroWrapRef = React.useRef(null);
  useEffect(() => {
    const applyClass = () => {
      const y = window.scrollY || 0;
      if (y < 120) {
        document.body.classList.add("orio-ai-hero-visible");
      } else {
        document.body.classList.remove("orio-ai-hero-visible");
      }
    };
    applyClass();
    window.addEventListener("scroll", applyClass, { passive: true });
    window.addEventListener("resize", applyClass);
    return () => {
      window.removeEventListener("scroll", applyClass);
      window.removeEventListener("resize", applyClass);
      document.body.classList.remove("orio-ai-hero-visible");
    };
  }, []);

  // Netflix-style hub redirect on cold app open, with 30-minute idle window.
  // • Fresh launch after >30min inactivity → send user to /hub so they see
  //   the hero carousel + profile picker.
  // • Within 30min of last activity → skip the hub, drop straight into Home.
  // • Cookie/JWT expiry (7 days) is handled by <Protected> which redirects
  //   to /login on 401 responses — the hub is not a login gate.
  useEffect(() => {
    if (!user) return;
    try {
      const last = parseInt(localStorage.getItem("orio_last_active") || "0", 10);
      const now = Date.now();
      const idle30min = !last || now - last > 30 * 60 * 1000;
      const seenThisSession = sessionStorage.getItem("orio_hub_seen") === "1";
      if (idle30min && !seenThisSession) {
        sessionStorage.setItem("orio_hub_seen", "1");
        navigate("/hub", { replace: true });
        return;
      }
      // Otherwise refresh the "last active" heartbeat so the next cold open
      // can compute idle time correctly.
      localStorage.setItem("orio_last_active", String(now));
    } catch { /* storage disabled — skip hub redirect */ }
  }, [user, navigate]);

  // Heartbeat: every time the tab becomes visible OR the user navigates a
  // route inside the app, refresh the "last active" timestamp so we don't
  // pop the hub on quick app switches.
  useEffect(() => {
    const beat = () => {
      try { localStorage.setItem("orio_last_active", String(Date.now())); } catch {}
    };
    beat();
    const onVis = () => { if (document.visibilityState === "visible") beat(); };
    document.addEventListener("visibilitychange", onVis);
    const interval = setInterval(beat, 60 * 1000); // once per minute while open
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      clearInterval(interval);
    };
  }, []);

  const [stats, setStats] = useState({ dogs: 0, matches: 0, missions: 0, emergencies: 0, shelters: 0 });
  const [reminders, setReminders] = useState([]);
  const [walking, setWalking] = useState(null);
  const [recent, setRecent] = useState([]);
  const [karma, setKarma] = useState(null);
  const [radarSummary, setRadarSummary] = useState(null);
  // AI briefing context — mirrors ORIO AI's live snapshot (karma, walks,
  // stories, notifs, etc.) so the HomeLiveStrip can render real numbers
  // without adding a new endpoint.
  const [aiCtx, setAiCtx] = useState(null);
  // City leaderboard rank + weekly quests progress — surfaced in HomeLiveStrip
  // instead of the old collapsible sections.
  const [cityRank, setCityRank] = useState(null);
  const [cityName, setCityName] = useState(null);
  const [quests, setQuests] = useState({ completed: 0, total: 0 });
  const [quickHazardOpen, setQuickHazardOpen] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);
  const [weather, setWeather] = useState(null);
  const [myDog, setMyDog] = useState(null);
  const [allDogs, setAllDogs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const loadAll = async () => {
      try {
        const [dogs, mt, miss, em, rem, wa, shelters, hazards, weath, km, rs] = await Promise.all([
          api.get("/dogs"),
          api.get("/match/matches"),
          api.get("/missions", { params: { kind: "mission" } }),
          api.get("/emergencies/active"),
          api.get("/reminders").catch(() => ({ data: [] })),
          api.get("/match/availability/me").catch(() => ({ data: { active: false } })),
          api.get("/shelters").catch(() => ({ data: [] })),
          api.get("/hazards").catch(() => ({ data: [] })),
          api.get("/weather/heat").catch(() => ({ data: null })),
          api.get("/karma/me").catch(() => ({ data: null })),
          api.get("/radar/summary", { params: user?.location ? { lat: user.location.lat, lng: user.location.lng } : {} }).catch(() => ({ data: null })),
        ]);
        const realDogs = dogs.data.filter((d) => !d.is_stub);
        setStats({
          dogs: realDogs.length,
          matches: mt.data.filter((m) => !m.expired).length,
          missions: miss.data.length,
          emergencies: em.data.length,
          shelters: (shelters.data || []).length,
        });
        setMyDog((prev) => prev || realDogs[0] || null);
        setAllDogs(realDogs);
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const upcoming = (rem.data || [])
          .filter((r) => !r.completed_at)
          .map((r) => ({ ...r, _days: Math.ceil((new Date(r.due_date) - today) / 86400000) }))
          .filter((r) => r._days <= 14)
          .sort((a, b) => a._days - b._days);
        setReminders(upcoming);
        setWalking(wa.data?.active ? wa.data : null);
        setWeather(weath.data || null);
        setKarma(km.data || null);
        setRadarSummary(rs.data || null);
        // Fetch AI briefing context (karma total, walks/km 7gg, stories 30gg,
        // upcoming reminders, unread notifs). Silently ignored on error.
        api.get("/orio-ai/briefing")
          .then((r) => setAiCtx(r?.data?.context || null))
          .catch(() => {});
        // City leaderboard — find own rank so we can render it as a pill in
        // HomeLiveStrip.
        api.get("/leaderboard/city")
          .then((r) => {
            const me = user?.id;
            const list = r?.data?.top || [];
            const mine = list.find((x) => x.user_id === me);
            if (mine) setCityRank(mine.rank);
            setCityName(r?.data?.city || null);
          })
          .catch(() => {});
        // Weekly quests progress — completed/total for the strip pill.
        api.get("/quests/me")
          .then((r) => {
            const qs = r?.data?.quests || [];
            setQuests({
              completed: qs.filter((q) => q.complete).length,
              total: qs.length,
            });
          })
          .catch(() => {});

        // Merge emergencies + missions + hazards into a "recent activity" feed
        const merged = [
          ...(em.data || []).map((e) => ({
            id: `em-${e.id}`,
            kind: "emergency",
            title: e.dog_name ? `${e.dog_name} smarrito` : "Cane smarrito",
            sub: e.last_seen_note || "Allerta attiva",
            ts: e.created_at,
            distance: e.distance_km,
            to: "/app/mappa",
          })),
          ...(miss.data || []).slice(0, 4).map((m) => ({
            id: `m-${m.id}`,
            kind: "mission",
            title: m.title,
            sub: m.description?.slice(0, 60) || "Missione attiva",
            ts: m.created_at,
            distance: m.distance_km,
            to: "/app/missioni",
          })),
          ...(hazards.data || []).slice(0, 3).map((h) => ({
            id: `h-${h.id}`,
            kind: "hazard",
            title: h.title || "Pericolo segnalato",
            sub: h.description?.slice(0, 60) || "Hazard nella zona",
            ts: h.created_at,
            distance: h.distance_km,
            to: "/app/mappa",
          })),
        ].sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0)).slice(0, 6);
        setRecent(merged);
      } catch {/* ignore */}
      finally { setLoaded(true); }
  };

  useOrioRefresh(loadAll, {
    events: [ORIO_EVENTS.REMINDERS_CHANGED, ORIO_EVENTS.DOG_CHANGED],
    refreshOnPath: "/app",
  });

  const firstName = user?.name?.split(" ")[0] || "amico";
  const dogName = stats.dogs > 0 ? "il tuo cane" : "Blue"; // placeholder when no dog yet

  if (!loaded) {
    return (
      <div className="px-4 sm:px-6 pt-4 pb-32 max-w-3xl mx-auto w-full min-w-0" data-testid="home-page-loading">
        <div className="h-4 w-24 rounded-full bg-[#F0EBE3] animate-pulse" />
        <div className="mt-6 grid grid-cols-3 gap-2 sm:gap-3">
          {[0,1,2].map((i) => (
            <div key={i} className="aspect-[3/2] rounded-3xl bg-white border border-[#F0EBE3] animate-pulse" />
          ))}
        </div>
        <div className="mt-6 h-40 rounded-3xl bg-white border border-[#F0EBE3] animate-pulse" />
        <div className="mt-6 space-y-3">
          {[0,1,2].map((i) => (
            <div key={i} className="h-16 rounded-2xl bg-white border border-[#F0EBE3] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* STORIES RIBBON — Instagram-style round avatars sticky under the
          top bar. Always visible so nearby users' unseen stories are
          one tap away. Renders nothing if the feed is empty. */}
      <div
        className="sticky top-0 z-30 -mx-4 sm:-mx-6"
        data-testid="stories-ribbon-wrap"
      >
        <HomeStoriesRibbon />
      </div>

      {/* ORIO AI HERO — full-bleed block frame occupying ~82vh at the top.
          Gemini-style: only ORIO O animated + prompt bar + subtle greeting.
          All welcome/daily-report info surface as rotating placeholders. */}
      <div className="-mx-4 sm:-mx-6" ref={heroWrapRef}>
        <OrioAIHero
          user={user}
          myDog={myDog}
          weather={weather}
          radarSummary={radarSummary}
          reminders={reminders}
        />
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.35 }}
        className="px-4 sm:px-6 pt-6 sm:pt-8 pb-10 max-w-3xl mx-auto w-full min-w-0"
        data-testid="home-page"
      >
      {/* STORIES STRIP first — right after the AI hero, per user's request:
          scrolling down out of the hero should immediately reveal the stories.
          Negative margin-top overlaps the hero's bottom-fade so the two
          sections blend seamlessly instead of hard-cutting. */}
      <div style={{ marginTop: "-56px", position: "relative", zIndex: 2 }}>
        <HomeStoriesStrip />
      </div>

      {/* FUNZIONI PRINCIPALI — TR-style horizontal-scroll big tiles with
          real ORIO illustrations. Falls back to warm gradient if missing. */}
      <HomeFeatureGallery
        testid="home-gallery-core"
        eyebrow="Funzioni principali"
        title="Tutto quello che serve al tuo cane"
        items={[
          { id: "sos", title: "SOS", sub: "Allerta la community in un tap",
            icon: Siren, to: "/app/mappa?tab=sos",
            img: orioIllustration("feat-sos"),
            gradient: "linear-gradient(135deg, #FF3B1A 0%, #FF7A1A 100%)" },
          { id: "missioni", title: "Missioni", sub: "Aiuta cani in cerca di casa",
            icon: Heart, to: "/app/missioni",
            img: orioIllustration("feat-missions"),
            gradient: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)" },
          { id: "segnalazioni", title: "Segnalazioni", sub: "Pericoli e bocconi",
            icon: AlertOctagon, to: "/app/mappa?tab=segnalazioni",
            img: orioIllustration("tip-hazard"),
            gradient: "linear-gradient(135deg, #E9542A 0%, #FF9E45 100%)" },
          { id: "vet", title: "Veterinari", sub: "Trova il più vicino",
            icon: Stethoscope, to: "/app/mappa?tab=veterinari",
            img: orioIllustration("feat-vet"),
            gradient: "linear-gradient(135deg, #5B9EE0 0%, #7B61FF 100%)" },
          { id: "cassetto", title: "Cassetto digitale", sub: "Passaporto, vaccini, esami",
            icon: Archive, to: "/app/cassetto",
            img: orioIllustration("feat-drawer"),
            gradient: "linear-gradient(135deg, #8E3A0E 0%, #C25612 100%)" },
          { id: "walk", title: "ORIO Walk", sub: "Registra la passeggiata",
            icon: Compass, to: "/app/passeggiate",
            img: orioIllustration("feat-walk"),
            gradient: "linear-gradient(135deg, #34D399 0%, #10B981 100%)" },
        ]}
      />

      {/* SOCIAL — Storie, Match, Park Room, Corsi & Educatori */}
      <HomeFeatureGallery
        testid="home-gallery-social"
        eyebrow="Social"
        title="Vivi ORIO con altri padroni"
        items={[
          { id: "stories", title: "Storie", sub: "Il TikTok dei cani",
            icon: Sparkles, to: "/app/match?tab=stories",
            img: orioIllustration("news-stories"),
            gradient: "linear-gradient(135deg, #FF7A1A 0%, #E9542A 100%)" },
          { id: "match", title: "Match", sub: "Compagni di passeggiata",
            icon: MessageSquareHeart, to: "/app/match",
            img: orioIllustration("feat-match"),
            gradient: "linear-gradient(135deg, #FF3B1A 0%, #FF9E45 100%)" },
          { id: "parkroom", title: "Park Room", sub: "Chi c'è al parco ora",
            icon: TreePine, to: "/app/match?tab=parkroom",
            img: orioIllustration("match-park-v2"),
            gradient: "linear-gradient(135deg, #059669 0%, #34D399 100%)" },
          { id: "corsi", title: "Corsi & Educatori", sub: "Professionisti verificati",
            icon: GraduationCap, to: "/app/match?tab=corsi",
            img: orioIllustration("match-educator-v2"),
            gradient: "linear-gradient(135deg, #7B61FF 0%, #A78BFA 100%)" },
        ]}
      />

      {/* LIVE STRIP — pulse of the user's ORIO life. Renders karma total,
          city rank, weekly quests progress, active reminders, walks/km,
          unread notifs, stories 30gg. Empty state auto-hides itself. */}
      <HomeLiveStrip
        karma={aiCtx?.karma_total ?? (karma?.balance ?? null)}
        remindersActive={reminders.length}
        nextReminder={reminders[0] ? { title: reminders[0].title } : null}
        walksWeek={aiCtx?.walks_week || 0}
        kmWeek={aiCtx?.km_week || 0}
        storiesWeek={aiCtx?.stories_published_30d || 0}
        unreadNotifs={aiCtx?.unread_notifications || 0}
        streakDays={null}
        cityRank={cityRank}
        cityName={cityName}
        questsCompleted={quests.completed}
        questsTotal={quests.total}
      />

      {/* QUICK ACTIONS — Cassetto + Aggiungi (always when has dog) + Dog switcher (only ≥2 dogs) */}
      {myDog && (
        <div data-testid="home-dog-switcher" className="mt-4 -mx-4 sm:-mx-6 px-4 sm:px-6">
          <div className="flex gap-2 overflow-x-auto orio-h-scroll pb-1">
            {allDogs.length > 1 && allDogs.map((d) => {
              const active = myDog?.id === d.id;
              return (
                <button
                  key={d.id}
                  data-testid={`home-dog-chip-${d.id}`}
                  onClick={() => setMyDog(d)}
                  className="shrink-0 inline-flex items-center gap-2 pl-1.5 pr-3.5 py-1.5 rounded-full text-[12px] font-medium border transition-all active:scale-95"
                  style={{
                    background: active ? "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)" : "#FFFFFF",
                    borderColor: active ? "transparent" : "#F0EBE3",
                    color: active ? "#FFFFFF" : "#5C5C5C",
                    boxShadow: active ? "0 6px 18px -4px rgba(255,122,26,0.45)" : "none",
                  }}
                >
                  <span className="h-6 w-6 rounded-full overflow-hidden bg-[#F4EFE6] border border-white/40 shrink-0">
                    {d.photo_url ? (
                      <img src={d.photo_url} alt={d.name} className="h-full w-full object-cover" />
                    ) : (
                      <span className="h-full w-full flex items-center justify-center text-[10px] font-display text-[#FF7A1A]">
                        {d.name?.charAt(0)?.toUpperCase() || "🐾"}
                      </span>
                    )}
                  </span>
                  {d.name}
                </button>
              );
            })}
            <Link
              to="/app/cassetto"
              data-testid="home-open-cassetto"
              className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white border border-[#F0EBE3] text-[12px] text-[#1A1A1A] font-medium hover:bg-[#F4EFE6] transition-all"
            >
              <BookOpen className="h-3.5 w-3.5 text-[#FF7A1A]" /> Cassetto
            </Link>
            <button
              type="button"
              data-testid="home-open-quick-hazard"
              onClick={() => { try { window.dispatchEvent(new CustomEvent("orio:open-quick-hazard")); } catch { /* noop */ } setQuickHazardOpen(true); }}
              className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[12px] font-semibold text-white transition-all"
              style={{
                background: "linear-gradient(135deg, #FFB020 0%, #FF3B30 100%)",
                boxShadow: "0 6px 18px -6px rgba(255,176,32,0.55)",
              }}
            >
              <Camera className="h-3.5 w-3.5" /> Segnala
            </button>
            <Link
              to="/app/cani"
              data-testid="home-dog-add"
              className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#FAF7F0] border border-dashed border-[#D8CFB6] text-[12px] text-[#7A7A7A] hover:text-[#1A1A1A] hover:bg-white"
            >
              <PawPrint className="h-3 w-3" /> Aggiungi
            </Link>
          </div>
        </div>
      )}

      {/* WALKING ROW — only if active */}
      {walking && (
        <Link
          to="/app/match"
          data-testid="home-match-walking-row"
          className="mt-5 block rounded-3xl p-4 sm:p-5 border overflow-hidden relative"
          style={{
            background: "linear-gradient(135deg, #1A2421 0%, #0F1614 100%)",
            borderColor: "#243831",
            boxShadow: "0 8px 30px -8px rgba(15,22,20,0.40)",
          }}
        >
          <div className="flex items-center gap-3 text-white">
            <div className="h-10 w-10 rounded-2xl bg-[#2DBA60]/20 text-[#2DBA60] flex items-center justify-center">
              <HeartHandshake className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#2DBA60]/85 font-medium">Match walking · attivo</div>
              <div className="font-display text-lg leading-tight mt-0.5">Sei visibile ad altri padroni</div>
              <div className="text-[12px] text-white/65 mt-0.5">Raggio {walking.radius_km || 5} km · termina alle {new Date(walking.expires_at).toLocaleTimeString("it-IT", {hour:'2-digit',minute:'2-digit'})}</div>
            </div>
            <ChevronRight className="h-5 w-5 text-white/40" />
          </div>
        </Link>
      )}

      {/* DAILY REPORT removed — the info (walking window, overdue reminders,
          zone alerts) is now surfaced as rotating placeholders in the ORIO AI
          hero at the top of Home. */}

      {/* WEEKEND BOOST — 2x karma banner shown only on Sat/Sun */}
      <WeekendBoost active={new Date().getDay() === 0 || new Date().getDay() === 6} />

      {/* 3 STAT TILES */}
      <div className="mt-5 flex gap-2.5 sm:gap-3">
        <StatTile
          icon={HeartHandshake}
          iconColor="#FF7A1A"
          value={stats.missions}
          label="Missioni attive"
          testid="home-stat-missions"
        />
        <StatTile
          icon={PawPrint}
          iconColor="#2DBA60"
          value={stats.dogs}
          label="Cani aiutati"
          testid="home-stat-dogs"
        />
        <StatTile
          icon={HomeIcon}
          iconColor="#F4B83C"
          value={stats.shelters}
          label="Rifugi connessi"
          testid="home-stat-shelters"
        />
      </div>

      {/* HEAT WARNING - vertical card (image top + text below), full width */}
      <div className="mt-4 sm:mt-5">
        <HeatWarning />
      </div>

      {/* WALKING TRACKER — daily-driver Pro feature. Big TR-style card. */}
      <WalkingBanner myDog={myDog} isPro={isPro} />

      {/* PRO UPSELL — collapsible */}
      {user && !user.subscription?.active && (
        <HomeSection id="pro-upsell" eyebrow="Upgrade" title="Sblocca Pro" accentColor="#FF7A1A" defaultOpen={false}>
          <PlanComparison variant="inline" onSelectPro={() => navigate("/billing")} />
        </HomeSection>
      )}

      {/* SAFE WALK + APP FEATURES — unified TR-style gallery.
          Uses the same horizontal-scroll big-tile system as the other
          Home galleries so the page reads as one coherent product. */}
      <HomeFeatureGallery
        testid="home-features-gallery"
        eyebrow="Per te oggi"
        title={myDog ? `Cosa puoi fare con ${myDog.name}` : "Inizia con ORIO"}
        items={buildFeatureSlides({ myDog, weather, stats }).map((s) => ({
          id: s.testid,
          title: s.title,
          sub: s.body,
          icon: s.ctaIcon,
          chip: s.eyebrow,
          to: s.onClickKey ? undefined : s.to,
          onClick: s.onClickKey === "tips" ? () => setTipsOpen(true) : undefined,
          img: orioIllustration(s.photo),
          gradient: s.gradient,
        }))}
      />

      {/* SAPEVI CHE — educational tips, TR-style gallery. */}
      <HomeFeatureGallery
        testid="home-tips-gallery"
        eyebrow="Sapevi che..."
        title="Scopri ORIO"
        items={TIPS.map((t) => ({
          id: t.id,
          title: t.title,
          sub: t.body,
          icon: t.icon,
          chip: t.stat ? `${t.stat} · ${t.statLabel || ""}`.trim().replace(/·\s*$/, "").trim() : undefined,
          to: t.to,
          img: orioIllustration(t.slug),
          gradient: `linear-gradient(135deg, ${t.color} 0%, ${t.color}AA 100%)`,
        }))}
      />

      {/* NOVITÀ — TR-style gallery della sezione "Cosa c'è di nuovo". */}
      <HomeFeatureGallery
        testid="home-news-gallery"
        eyebrow="Le ultime novità"
        title="Cosa c'è di nuovo"
        items={NEWS_SLIDES.map((n) => ({
          id: n.id,
          title: `${n.title}${n.highlight ? " " + n.highlight : ""}`.trim(),
          sub: n.body,
          icon: Sparkles,
          chip: n.tag,
          to: n.to,
          img: orioIllustration(n.slug),
          gradient: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
        }))}
      />

      {/* ULTIME SEGNALAZIONI */}
      {recent.length > 0 && (
        <Section
          eyebrow="Attività"
          title="Ultime segnalazioni"
          action={<Link to="/app/missioni" className="text-[13px] text-[#FF7A1A] font-medium">Vedi tutte</Link>}
        >
          <List>
            {recent.slice(0, 4).map((r) => (
              <Row
                key={r.id}
                icon={r.kind === "emergency" ? AlertTriangle : r.kind === "mission" ? HeartHandshake : Bell}
                iconColor={r.kind === "emergency" ? "#E94545" : r.kind === "mission" ? "#FF7A1A" : "#F4B83C"}
                label={r.title}
                sub={`${ago(r.ts)}${r.distance ? " · " + r.distance.toFixed(1) + " km" : ""}`}
                to={r.to}
              />
            ))}
          </List>
        </Section>
      )}

      {/* ORIO PRO BANNER — cream/warm with golden retriever */}
      {!isPro && (
        <Section className="mt-8">
          <div
            data-testid="home-pro-banner"
            className="orio-card-warm rounded-3xl p-5 sm:p-6 relative overflow-hidden"
          >
            <div className="relative z-10 max-w-[62%]">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#C25612] font-semibold">
                <Crown className="h-3 w-3" /> ORIO PRO
              </div>
              <h3 className="mt-2 font-display text-[22px] sm:text-2xl leading-tight text-[#1A1A1A]">
                30 giorni gratis,<br />
                poi <span className="text-[#FF7A1A]">€4.99/mese</span>.
              </h3>
              <ul className="mt-3 space-y-1.5 text-[13px] text-[#6B6B6B]">
                <li className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-[#FF7A1A]" /> SOS illimitato</li>
                <li className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-[#FF7A1A]" /> Funzioni avanzate</li>
                <li className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-[#FF7A1A]" /> Nessuna pubblicità</li>
              </ul>
              {canStartTrial ? (
                <button
                  onClick={startTrial}
                  data-testid="home-trial-start"
                  className="mt-4 orio-cta-btn px-5 py-2.5 rounded-full text-sm font-semibold"
                >
                  Inizia gratis
                </button>
              ) : (
                <Link
                  to="/billing"
                  data-testid="home-pro-cta"
                  className="mt-4 inline-block orio-cta-btn px-5 py-2.5 rounded-full text-sm font-semibold"
                >
                  Scopri di più
                </Link>
              )}
            </div>
            {/* Dog illustration (right-aligned) — woman walking dog on beach (premium lifestyle) */}
            <div
              className="absolute -right-2 -bottom-2 w-[150px] h-[150px] sm:w-[180px] sm:h-[180px] pointer-events-none opacity-95"
              style={{
                backgroundImage: `url(${orioIllustration("pro-banner")})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                WebkitMaskImage: "radial-gradient(circle at center, #000 65%, transparent 80%)",
                maskImage: "radial-gradient(circle at center, #000 65%, transparent 80%)",
              }}
            />
          </div>
        </Section>
      )}

      {/* SHELTER CTA — end-of-scroll banner. Invites owners with a rifugio
          to register on ORIO (or discover shelters if they don't run one).
          Renders for everyone; the CTA copy adapts to whether the user is
          already registered as a shelter. */}
      <Section className="mt-8">
        <Link
          to={user?.is_shelter ? "/app/rifugio" : "/app/rifugio/registra"}
          data-testid="home-shelter-banner"
          className="block relative rounded-3xl overflow-hidden active:scale-[0.99] transition-transform"
          style={{
            background: "linear-gradient(135deg, #F5EEE0 0%, #EADCBF 45%, #E8D3B5 100%)",
            border: "1px solid rgba(139, 106, 20, 0.22)",
            boxShadow: "0 22px 46px -22px rgba(139,106,20,0.35), 0 3px 8px -2px rgba(0,0,0,0.05)",
            minHeight: 220,
          }}
        >
          {/* Painterly shelter illustration (right-anchored) */}
          <div
            aria-hidden
            className="absolute right-0 top-0 bottom-0 w-[48%] pointer-events-none overflow-hidden"
            style={{
              backgroundImage: `url(${orioIllustration("shelter-hero")})`,
              backgroundSize: "cover",
              backgroundPosition: "center right",
              WebkitMaskImage: "linear-gradient(90deg, transparent 0%, #000 40%)",
              maskImage: "linear-gradient(90deg, transparent 0%, #000 40%)",
            }}
          />
          <div className="relative z-10 p-5 sm:p-6 max-w-[60%]">
            <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#8B6A14]">
              <HeartHandshake className="h-3 w-3" /> Rifugi ORIO
            </div>
            <h3 className="mt-2 font-display text-[22px] sm:text-2xl leading-tight text-[#1A1A1A] tracking-tight"
                style={{ fontWeight: 700 }}>
              {user?.is_shelter
                ? "Gestisci il tuo rifugio"
                : "Hai un rifugio? Apri un profilo ORIO"}
            </h3>
            <p className="mt-2 text-[13px] text-[#4A4A4A] leading-snug">
              {user?.is_shelter
                ? "Pubblica cani in adozione, ricevi missioni dalla community, aggiorna il tuo profilo pubblico."
                : "Compari sulla mappa dopo la verifica, pubblichi cani in adozione e ricevi aiuti concreti dalla community."}
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-white"
                 style={{
                   background: "linear-gradient(135deg, #C77F00 0%, #8B6A14 100%)",
                   boxShadow: "0 10px 22px -8px rgba(139,106,20,0.55)",
                 }}>
              {user?.is_shelter ? "Vai al rifugio" : "Registra il rifugio"}
              <ArrowRight className="h-3.5 w-3.5" />
            </div>
          </div>
        </Link>
      </Section>

      {/* PROFESSIONAL CTA — locked banner for educators & veterinarians.
          Feature not yet available: rendered slightly dimmed with a "Presto
          disponibile" pill and NOT clickable. Serves as the final visual
          anchor of the Home so the scroll ends cleanly here. */}
      <Section className="mt-6 mb-2">
        <div
          data-testid="home-professional-banner"
          aria-disabled="true"
          className="relative rounded-3xl overflow-hidden select-none cursor-default"
          style={{
            background: "linear-gradient(135deg, #E4E3DE 0%, #D2CFC6 50%, #BFBBB0 100%)",
            border: "1px solid rgba(60, 55, 45, 0.20)",
            boxShadow: "0 22px 46px -22px rgba(60,55,45,0.35), 0 3px 8px -2px rgba(0,0,0,0.05)",
            minHeight: 220,
            opacity: 0.78,
            filter: "grayscale(0.35)",
          }}
        >
          {/* Right-anchored illustration */}
          <div
            aria-hidden
            className="absolute right-0 top-0 bottom-0 w-[48%] pointer-events-none overflow-hidden"
            style={{
              backgroundImage: `url(${orioIllustration("match-educator-v2")})`,
              backgroundSize: "cover",
              backgroundPosition: "center right",
              WebkitMaskImage: "linear-gradient(90deg, transparent 0%, #000 40%)",
              maskImage: "linear-gradient(90deg, transparent 0%, #000 40%)",
              filter: "grayscale(0.5)",
            }}
          />
          {/* Lock overlay chip top-right */}
          <div className="absolute top-3 right-3 z-10">
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9.5px] font-bold uppercase tracking-[0.20em] text-white"
              style={{
                background: "rgba(60,55,45,0.72)",
                backdropFilter: "blur(8px)",
              }}
            >
              <Lock className="h-3 w-3" /> Presto disponibile
            </span>
          </div>
          <div className="relative z-10 p-5 sm:p-6 max-w-[62%]">
            <div className="inline-flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#4A4238]">
              <GraduationCap className="h-3 w-3" /> Professionisti
            </div>
            <h3
              className="mt-2 font-display text-[22px] sm:text-2xl leading-tight text-[#1A1A1A] tracking-tight"
              style={{ fontWeight: 700 }}
            >
              Sei educatore o veterinario?
            </h3>
            <p className="mt-2 text-[13px] text-[#4A4A4A] leading-snug">
              Presto potrai aprire un profilo professionale ORIO — verificato, con
              recensioni, prenotazioni dirette e visibilità sulla mappa.
            </p>
            <div
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-white/95"
              style={{
                background: "linear-gradient(135deg, #6B655A 0%, #4A4238 100%)",
                boxShadow: "0 10px 22px -8px rgba(60,55,45,0.45)",
              }}
            >
              In arrivo
            </div>
          </div>
        </div>
      </Section>

      {/* Quick hazard capture — 1-tap report */}
      <QuickHazardCapture
        open={quickHazardOpen}
        onClose={() => setQuickHazardOpen(false)}
        onDone={() => setQuickHazardOpen(false)}
      />

      {/* Tips guide — opened from the last FeatureSlide */}
      <TipsSheet open={tipsOpen} onClose={() => setTipsOpen(false)} />
    </motion.div>
    </>
  );
}

/* WalkingBanner — big Trade-Republic style card that opens the Walking Tracker.
   Shows this-week stats for Pro users, a warm "Prova ORIO Walk" preview for Free.
   Painterly ORIO illustration on the right, headline on the left. */
function WalkingBanner({ myDog, isPro }) {
  const [stats, setStats] = React.useState(null);
  const [activeWalk, setActiveWalk] = React.useState(null);
  React.useEffect(() => {
    if (!isPro) return;
    let mounted = true;
    (async () => {
      try {
        const [{ data: s }, { data: a }] = await Promise.all([
          api.get("/walks/stats"),
          api.get("/walks/active"),
        ]);
        if (!mounted) return;
        setStats(s);
        setActiveWalk(a?.active ? a : null);
      } catch { /* silent */ }
    })();
    return () => { mounted = false; };
  }, [isPro]);

  const km = stats ? (stats.week.distance_m / 1000).toFixed(1) : "0.0";
  const streak = stats?.streak_day || 0;
  const ctaLabel = activeWalk ? "Riprendi passeggiata" : `Esci con ${myDog?.name || "il tuo cane"}`;

  return (
    <div className="mt-5 sm:mt-6" data-testid="home-walking-banner">
      <Link
        to="/app/passeggiate"
        onClick={hapticTap}
        className="block relative rounded-[28px] overflow-hidden border active:scale-[0.99] transition-transform"
        style={{
          borderColor: "rgba(255,122,26,0.28)",
          background: "linear-gradient(135deg, #FFF6E6 0%, #FFEDD5 45%, #FFE4D2 100%)",
          boxShadow: "0 22px 48px -22px rgba(255,122,26,0.55), 0 3px 8px -2px rgba(0,0,0,0.06)",
          minHeight: 200,
        }}
      >
        <div
          aria-hidden
          className="absolute right-0 top-0 bottom-0 w-[46%] pointer-events-none overflow-hidden"
          style={{
            WebkitMaskImage: "radial-gradient(circle at 80% 50%, #000 55%, transparent 90%)",
            maskImage: "radial-gradient(circle at 80% 50%, #000 55%, transparent 90%)",
          }}
        >
          <IlloImg slug="feat-walk" loading="eager" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: "center right" }} />
        </div>

        {activeWalk && (
          <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[10px] font-bold uppercase tracking-[0.16em] text-[#E94545]"
               style={{ boxShadow: "0 4px 14px -4px rgba(233,69,69,0.35)" }}>
            <span className="h-1.5 w-1.5 rounded-full bg-[#E94545] animate-pulse" />
            in corso
          </div>
        )}

        <div className="relative z-[1] p-5 sm:p-6 max-w-[62%]">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold inline-flex items-center gap-1.5"
               style={{ color: "#C25612" }}>
            <PawPrint className="h-3 w-3" /> ORIO Walk
            {!isPro && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] text-white font-bold"
                    style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #FFB020 100%)" }}>
                PRO
              </span>
            )}
          </div>
          <h3 className="mt-2 font-display text-[24px] sm:text-[28px] leading-[1.05] tracking-tight text-[#1A1A1A]">
            {activeWalk ? "Riprendi la passeggiata." : (
              <>Stai uscendo con<br /><span className="orio-grad-text">{myDog?.name || "il tuo cane"}?</span></>
            )}
          </h3>

          {isPro && stats && (
            <div className="mt-3 flex items-center gap-3">
              <div>
                <div className="font-display text-lg tabular-nums font-bold leading-none text-[#1A1A1A]">
                  {km} <span className="text-sm text-[#7A7A7A] font-normal">km</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#7A7A7A] mt-0.5">Settimana</div>
              </div>
              {streak > 0 && (
                <div className="pl-3 border-l border-[#F0EBE3]">
                  <div className="font-display text-lg tabular-nums font-bold leading-none text-[#1A1A1A] inline-flex items-center gap-1">
                    <Flame className="h-3.5 w-3.5" style={{ color: "#FF7A1A" }} />{streak}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#7A7A7A] mt-0.5">Streak</div>
                </div>
              )}
            </div>
          )}
          {!isPro && (
            <p className="mt-2 text-[12.5px] text-[#6B6B6B] leading-snug max-w-[280px]">
              GPS, andatura, calorie e streak Karma. Come Apple Fitness, per te e per il tuo cane.
            </p>
          )}

          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-white text-[13px] font-semibold"
               style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
                        boxShadow: "0 14px 28px -10px rgba(233,69,69,0.55)" }}>
            <Play className="h-3.5 w-3.5" fill="white" /> {ctaLabel}
          </div>
        </div>
      </Link>
    </div>
  );
}


function Pin({ x, y, color }) {  return (
    <div
      className="absolute h-6 w-6 rounded-full flex items-center justify-center"
      style={{
        left: x, top: y, transform: "translate(-50%, -50%)",
        background: color, boxShadow: `0 4px 10px ${color}55, 0 0 0 3px #FFFFFF`,
      }}
    >
      <div className="h-1.5 w-1.5 rounded-full bg-white" />
    </div>
  );
}

function buildFeatureSlides({ myDog, weather, stats }) {
  const slides = [];

  // 1) If no dog yet, the most important next action is creating one.
  if (!myDog) {
    slides.push({
      to: "/app/cani/nuovo",
      testid: "feat-create-dog",
      eyebrow: "INIZIA QUI",
      title: "Crea il primo passaporto",
      body: "Bastano 60 secondi: foto, microchip, libretto e QR pubblico.",
      cta: "Aggiungi cane",
      ctaIcon: PawPrint,
      // Litter of cute puppies — emotional first impression for a new dog
      photo: "feat-create-dog",
      gradient: "linear-gradient(135deg, #FFF1DF 0%, #FFE5C2 50%, #FFD9A6 100%)",
      accent: "#C25612",
    });
  }

  // 2) If the dog has no microchip yet, prompt to add it.
  if (myDog && !myDog.microchip) {
    slides.push({
      to: `/app/cani/${myDog.id}`,
      testid: "feat-add-microchip",
      eyebrow: "PASSAPORTO INCOMPLETO",
      title: `Aggiungi microchip a ${myDog.name}`,
      body: "Il microchip è il dato più importante in caso di smarrimento.",
      cta: "Completa profilo",
      ctaIcon: Sparkles,
      // German shepherd being petted by owner — care & medical/ID context
      photo: "feat-microchip",
      gradient: "linear-gradient(135deg, #FFF4D3 0%, #FBE9B0 50%, #F5DC93 100%)",
      accent: "#8B6A14",
    });
  }

  // 3) ORIO Walk — the daily-driver Walking Tracker (GPS, calories, streak,
  //    karma, weekly report). NOT to be confused with Match walking mode
  //    (which is about meeting other dog owners — slide 4 below).
  slides.push({
    to: "/app/passeggiate",
    testid: "feat-walk",
    eyebrow: "ORIO WALK · PRO",
    title: myDog ? `In passeggiata con ${myDog.name}` : "In passeggiata col tuo cane",
    body: "GPS, km, andatura, calorie del cane e streak Karma. Come Apple Fitness, per voi.",
    cta: "Inizia passeggiata",
    ctaIcon: Footprints,
    photo: "feat-walk",
    gradient: "linear-gradient(135deg, #FFF1DF 0%, #FFE5C2 50%, #FFD9A6 100%)",
    accent: "#C25612",
  });

  // 3b) ORIO Radar — real-time environmental & health risk map layer.
  //      Shows a live count of active alerts (fires + weather + endemic zones).
  slides.push({
    to: "/app/mappa?radar=on",
    testid: "feat-radar",
    eyebrow: "ORIO RADAR · NUOVO",
    title: "Il radar sicurezza del tuo cane",
    body: "Incendi in tempo reale, allerte meteo Protezione Civile e zone endemiche (leishmaniosi, filaria, zecche) sulla mappa.",
    cta: "Apri il radar",
    ctaIcon: AlertTriangle,
    photo: "feat-radar",
    gradient: "linear-gradient(135deg, #FFEEDF 0%, #FCE0C1 50%, #F7CFA1 100%)",
    accent: "#C25612",
  });

  // 4) Match — finding friends.
  slides.push({
    to: "/app/match",
    testid: "feat-friends",
    eyebrow: "MATCH",
    title: "Trova compagni",
    body: "Cani e proprietari vicini per passeggiate in compagnia.",
    cta: "Inizia a fare swipe",
    ctaIcon: Heart,
    // Two dogs playing together on a grassy field — friendship between dogs
    photo: "feat-match",
    gradient: "linear-gradient(135deg, #E8F8EE 0%, #D6F0DF 50%, #BCE5C9 100%)",
    accent: "#1E7E3F",
  });

  // 5) Drawer — only if dog exists (otherwise irrelevant).
  if (myDog) {
    slides.push({
      to: "/app/cassetto",
      testid: "feat-drawer",
      eyebrow: "CASSETTO DIGITALE",
      title: "Documenti sempre con te",
      body: `Microchip, vaccini e visite di ${myDog.name}.`,
      cta: "Apri cassetto",
      ctaIcon: Briefcase,
      // Person petting happy dog outdoors — care & docs context
      photo: "feat-drawer",
      gradient: "linear-gradient(135deg, #FFF4D3 0%, #FBE9B0 50%, #F5DC93 100%)",
      accent: "#8B6A14",
    });
  }

  // 6) Missions — only if there are active missions nearby.
  if (stats?.missions > 0) {
    slides.push({
      to: "/app/missioni",
      testid: "feat-missions",
      eyebrow: "MISSIONI NELLA TUA ZONA",
      title: `${stats.missions} ${stats.missions === 1 ? "missione attiva" : "missioni attive"}`,
      body: "Aiuta un cane vicino a te in difficoltà.",
      cta: "Vedi missioni",
      ctaIcon: HeartHandshake,
      // Person playing with white and brown dog — help/rescue energy
      photo: "feat-missions",
      gradient: "linear-gradient(135deg, #FDECEC 0%, #FBD9D9 50%, #F8C2C2 100%)",
      accent: "#B82E2E",
    });
  }

  // 7) Find Vet — useful always.
  slides.push({
    to: "/app/mappa?filter=vet",
    testid: "feat-vet",
    eyebrow: "VETERINARIO 24/7",
    title: "Trova il vet più vicino",
    body: "Apertura, urgenze e contatto rapido in 2 tap.",
    cta: "Cerca veterinario",
    ctaIcon: Stethoscope,
    photo: "feat-vet",
    gradient: "linear-gradient(135deg, #E3F2F8 0%, #C9E5F0 50%, #ADD2E5 100%)",
    accent: "#2B7CE9",
  });

  // 8) Dog Parks nearby.
  slides.push({
    to: "/app/mappa?filter=park",
    testid: "feat-parks",
    eyebrow: "AREE CANI",
    title: "Parchi e aree sgambamento",
    body: "Aree recintate vicino a te, con orari e accesso.",
    cta: "Vedi parchi",
    ctaIcon: TreePine,
    photo: "feat-parks",
    gradient: "linear-gradient(135deg, #E8F8EE 0%, #D6F0DF 50%, #BCE5C9 100%)",
    accent: "#1E7E3F",
  });

  // 9) SOS demo / safety.
  slides.push({
    to: "/app/mappa",
    testid: "feat-sos",
    eyebrow: "EMERGENZA",
    title: "SOS in un tocco",
    body: "Allerta i vicini con onde 10 → 80 km.",
    cta: "Scopri come",
    ctaIcon: AlertTriangle,
    photo: "feat-sos",
    gradient: "linear-gradient(135deg, #FDECEC 0%, #FBD9D9 50%, #F8C2C2 100%)",
    accent: "#B82E2E",
  });

  // 10) LAST — Informative guide slide: "Come usare ORIO al meglio"
  //     Onclick opens the TipsSheet (handled in Home component via onOpenTips prop)
  slides.push({
    onClickKey: "tips",
    testid: "feat-tips-guide",
    eyebrow: "GUIDA ORIO",
    title: "Come usare ORIO al meglio",
    body: "Suggerimenti per padroni a spasso: passeggiata sicura, segnalazioni, cassetto, karma.",
    cta: "Scopri i suggerimenti",
    ctaIcon: BookOpen,
    photo: "tip-pro",
    gradient: "linear-gradient(135deg, #F1ECFF 0%, #E5DCFB 50%, #D2C2F4 100%)",
    accent: "#7B61FF",
  });

  // 11) COMMUNITY — the ORIO network in your zone.
  slides.push({
    to: "/app/mappa",
    testid: "feat-community",
    eyebrow: "COMMUNITY",
    title: "La rete ORIO nella tua zona",
    body: "Padroni verificati vicini a te. Più siete, più il quartiere è sicuro per i cani.",
    cta: "Vedi chi è vicino",
    ctaIcon: Users,
    photo: "feat-community-v2",
    gradient: "linear-gradient(135deg, #FDECDD 0%, #FBD6B5 50%, #F5C48D 100%)",
    accent: "#B5601D",
  });

  // 12) RIFUGIO — adoption discovery.
  slides.push({
    to: "/app/missioni?filter=shelter",
    testid: "feat-rifugio",
    eyebrow: "ADOZIONI",
    title: "Rifugi & cani adottabili",
    body: "Sfoglia i cani in cerca di famiglia nei rifugi vicini. Storia, carattere, esigenze.",
    cta: "Scopri i rifugi",
    ctaIcon: HeartHandshake,
    photo: "feat-rifugio-v2",
    gradient: "linear-gradient(135deg, #FBEBE0 0%, #F5D2BE 50%, #EDB396 100%)",
    accent: "#9B4E1F",
  });

  // 13) KARMA — engagement / social proof.
  slides.push({
    to: "/app/profilo",
    testid: "feat-karma",
    eyebrow: "PROGRESSO",
    title: "Il tuo Karma cresce",
    body: "Ogni gesto conta: segnala un pericolo, aiuta una missione, invita un amico.",
    cta: "Vedi i tuoi obiettivi",
    ctaIcon: Sparkles,
    photo: "feat-karma-v2",
    gradient: "linear-gradient(135deg, #FFF4D3 0%, #FBE9B0 50%, #F5DC93 100%)",
    accent: "#8B6A14",
  });

  return slides;
}

/* ─── EDUCATIONAL TIPS — "Sapevi che…" carousel (v20 — TR-style big vertical cards) ─────── */
/* ============================================================
   NEWS_SLIDES — carosello "Cosa c'è di nuovo" in Home.
   Ordine: più recente prima. Ogni slide ha slug immagine ORIO,
   titolo, paragrafo esplicativo e CTA con deep-link.
   ============================================================ */
const NEWS_SLIDES = [
  {
    id: "n-stories",
    slug: "news-stories",
    eyebrow: "Reels · 12h · Karma",
    title: "ORIO",
    highlight: "Stories",
    body: "Racconta la passeggiata in 10 secondi. Guarda cosa fanno gli altri cani in giro adesso, reagisci con un ❤️ o un 🐾 e trasforma una visualizzazione in un match. Le storie durano 12 ore e poi spariscono.",
    cta: "Apri Stories",
    to: "/app/match?tab=stories",
    tag: "Nuovo",
  },
  {
    id: "n-presence",
    slug: "feat-presence",
    eyebrow: "Mappa · 25km · Privacy-first",
    title: "Chi c'è",
    highlight: "online",
    body: "Attiva la visibilità e scopri chi c'è nei tuoi 25 km adesso. Vedi il loro cane, la distanza e puoi salutarli con un 👋: il saluto arriva direttamente nel loro Match.",
    cta: "Rendimi visibile",
    to: "/app/mappa?open=presence",
    tag: "Novità",
  },
  {
    id: "n-radar",
    slug: "feat-radar",
    eyebrow: "Sicurezza · Live · Multi-layer",
    title: "ORIO",
    highlight: "Radar",
    body: "Incendi Copernicus, allerte meteo Protezione Civile, zone endemiche di leishmaniosi e zecche, caldo estremo sull'asfalto. Configura i layer che ti interessano — il resto resta pulito.",
    cta: "Configura layer",
    to: "/app/mappa?open=radar",
    tag: "Aggiornato",
  },
];

function NewsSlide({ id, slug, eyebrow, title, highlight, body, cta, to, tag }) {
  return (
    <div
      data-testid={`news-slide-${id}`}
      className="shrink-0 snap-start w-[86vw] max-w-[360px] rounded-3xl overflow-hidden border"
      style={{
        background: "linear-gradient(135deg, #FFF6E6 0%, #FFE8CB 55%, #FCD9A6 100%)",
        borderColor: "#F0D9B0",
        boxShadow: "0 20px 40px -20px rgba(255,122,26,0.28), 0 4px 10px -4px rgba(0,0,0,0.05)",
      }}
    >
      {/* Hero image */}
      <div className="relative w-full overflow-hidden" style={{ aspectRatio: "5 / 3" }}>
        <img
          src={orioIllustration(slug)}
          onError={(e) => { e.currentTarget.src = orioIllustrationFallback(slug); }}
          alt={`${title} ${highlight}`}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] text-white"
             style={{
               background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
               boxShadow: "0 8px 18px -6px rgba(255,122,26,0.6)",
             }}>
          <Sparkles className="h-3 w-3" /> {tag}
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] font-semibold">
          {eyebrow}
        </div>
        <h3 className="mt-1 font-display text-2xl leading-tight text-[#1A1A1A]">
          {title} <span className="text-[#FF7A1A]">{highlight}</span>
        </h3>
        <p className="mt-2 text-[13.5px] text-[#4A4A4A] leading-relaxed line-clamp-4">
          {body}
        </p>
        <Link
          to={to}
          data-testid={`news-slide-${id}-cta`}
          className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13.5px] font-semibold text-white"
          style={{
            background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
            boxShadow: "0 12px 24px -8px rgba(255,122,26,0.55)",
          }}
        >
          {cta} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}

const TIPS = [
  { id: "t-sos", slug: "tip-sos", icon: AlertTriangle, color: "#E94545", title: "SOS a onde concentriche",
    body: "L'allerta parte a 10 km e cresce fino a 80 km in 10 minuti, raggiungendo solo chi è davvero vicino al tuo cane.", to: "/app/mappa",
    stat: "10 → 80 km", statLabel: "raggio dinamico" },
  { id: "t-community", slug: "tip-community-v2", icon: Users, color: "#B5601D", title: "Community verificata",
    body: "Ogni padrone ORIO ha verificato la propria email. In caso di emergenza sai che chi risponde è reale.", to: "/app/mappa",
    stat: "1k+", statLabel: "padroni verificati" },
  { id: "t-vet", slug: "tip-vet", icon: Stethoscope, color: "#2B7CE9", title: "Veterinario in 2 tap",
    body: "Dall'icona vet sulla mappa chiami direttamente il più vicino in apertura. Salvi minuti preziosi.", to: "/app/mappa?filter=vet",
    stat: "2 tap", statLabel: "per chiamare" },
  { id: "t-microchip", slug: "tip-microchip", icon: Sparkles, color: "#FF7A1A", title: "Microchip = il dato più importante",
    body: "Chi trova il tuo cane può leggere il microchip dal QR pubblico. Niente chiamate al canile, niente attese.", to: "/app/cani",
    stat: "1 QR", statLabel: "= identità completa" },
  { id: "t-match", slug: "tip-match", icon: Heart, color: "#34D399", title: "Match per camminare, non chattare",
    body: "Niente DM infinite: dopo il match si propone un orario per una vera passeggiata insieme.", to: "/app/match",
    stat: "10 msg", statLabel: "poi si esce" },
  { id: "t-cassetto", slug: "tip-cassetto", icon: Briefcase, color: "#8B6A14", title: "Cassetto digitale cifrato",
    body: "Libretto, vaccini e ricette sempre con te — anche offline. Solo tu (e chi autorizzi) puoi leggerli.", to: "/app/cassetto",
    stat: "AES-256", statLabel: "cifratura" },
  { id: "t-karma", slug: "tip-karma-v2", icon: Sparkles, color: "#F4B83C", title: "Karma per chi aiuta",
    body: "Ogni missione completata, segnalazione hazard e match generato accumula Karma. Sblocchi badge e visibilità.", to: "/app/profilo",
    stat: "+3", statLabel: "per ogni gesto" },
  { id: "t-walking", slug: "tip-safewalk-v2", icon: Footprints, color: "#FF7A1A", title: "Walking mode protetto",
    body: "Quando passeggi, ORIO ti avvisa di pericoli, cani in libertà e altri walker nei dintorni.", to: "/app/match",
    stat: "500m", statLabel: "raggio walking" },
  { id: "t-hazard", slug: "tip-hazard", icon: AlertTriangle, color: "#FFB020", title: "Segnala un pericolo (gratis)",
    body: "Vetro a terra, esca sospetta, cane senza guinzaglio: avvisa la community in 10 secondi. Anche da Free.", to: "/app/mappa",
    stat: "10 sec", statLabel: "per segnalare" },
  { id: "t-shelter", slug: "tip-shelter", icon: HeartHandshake, color: "#2DBA60", title: "Hai un rifugio?",
    body: "Registralo su ORIO: dopo la verifica compari sulla mappa, pubblichi cani in adozione e ricevi aiuti concreti dalla community.",
    to: "/app/rifugio/registra",
    stat: "Gratis", statLabel: "per sempre" },
  { id: "t-meteo", slug: "tip-meteo", icon: Sun, color: "#FF7A1A", title: "Meteo dog-friendly",
    body: "L'app legge la temperatura dell'asfalto: se brucia le zampe, te lo dice prima di uscire.", to: "/app",
    stat: "55°C", statLabel: "soglia asfalto" },
  { id: "t-emergenza", slug: "tip-emergenza-v2", icon: AlertTriangle, color: "#E94545", title: "Emergenza 24/7",
    body: "Il pulsante SOS attiva simultaneamente allerta community, vet più vicino e contatti d'emergenza.", to: "/app/mappa",
    stat: "1 tap", statLabel: "SOS totale" },
  { id: "t-qr", slug: "tip-qr", icon: QrCode, color: "#5B9EE0", title: "QR pubblico sul collare",
    body: "Stampa il QR del tuo cane: chi lo trova vede contatti emergenza, non dati sensibili.", to: "/app/cani",
    stat: "0 dati", statLabel: "sensibili esposti" },
  { id: "t-passaporto", slug: "tip-passaporto-v2", icon: FileText, color: "#7B61FF", title: "Passaporto digitale",
    body: "Ogni cane ORIO ha un passaporto pubblico condivisibile — utile in viaggio, dal vet o al parco.", to: "/app/cani",
    stat: "1 link", statLabel: "per tutto" },
  { id: "t-vetprof", slug: "tip-veterinario-v2", icon: Stethoscope, color: "#2B7CE9", title: "Vet a distanza",
    body: "In arrivo: video-consulto con veterinari partner ORIO, senza prendere appuntamento fisico.", to: "/app/mappa?filter=vet",
    stat: "Soon", statLabel: "in arrivo" },
  { id: "t-pro", slug: "tip-pro", icon: Crown, color: "#C77F00", title: "30 giorni gratis",
    body: "Tutte le funzioni sbloccate per un mese. Poi €4,99 al mese. Disdici quando vuoi.", to: "/app/profilo?tab=abbonamento",
    stat: "30 gg", statLabel: "gratis" },
];

function TipSlide({ id, slug, icon: Icon, color, title, body, to, stat, statLabel }) {
  return (
    <Link
      to={to}
      onClick={hapticTap}
      data-testid={`tip-${id}`}
      className="block relative rounded-[28px] overflow-hidden border border-[#F0EBE3] bg-white active:scale-[0.985] transition-transform"
      style={{
        width: "min(82vw, 340px)",
        minHeight: 470,
        boxShadow: `0 22px 44px -20px ${color}30, 0 2px 6px -2px rgba(0,0,0,0.05)`,
      }}
    >
      {/* TOP — big hero illustration (Trade Republic style, ~55% of card) */}
      {slug && (
        <div className="relative w-full h-[260px] overflow-hidden" style={{ background: `${color}10` }}>
          <IlloImg slug={slug} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          <div aria-hidden className="absolute inset-x-0 bottom-0 h-16"
            style={{ background: "linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.85) 100%)" }} />
          <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[9.5px] uppercase tracking-[0.20em] font-bold"
               style={{ color, boxShadow: "0 4px 12px -4px rgba(0,0,0,0.15)" }}>
            <span className="h-1 w-1 rounded-full animate-pulse" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
            Sapevi che…
          </div>
          {stat && (
            <div className="absolute top-4 right-4 px-3 py-1.5 rounded-2xl bg-white/95 backdrop-blur text-right"
                 style={{ boxShadow: "0 6px 16px -4px rgba(0,0,0,0.15)" }}>
              <div className="font-display text-base font-bold tabular-nums leading-none" style={{ color }}>{stat}</div>
              {statLabel && <div className="text-[8.5px] uppercase tracking-[0.14em] text-[#7A7A7A] mt-0.5">{statLabel}</div>}
            </div>
          )}
        </div>
      )}
      {/* BOTTOM — TR content panel */}
      <div className="px-5 pt-5 pb-5 relative">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-2xl flex items-center justify-center shrink-0"
            style={{ background: `${color}18`, color }}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-[19px] leading-[1.15] text-[#0F0F0F] tracking-tight">
              {title}
            </div>
            <p className="text-[12.5px] text-[#6B6B6B] mt-1.5 leading-snug line-clamp-3">{body}</p>
          </div>
        </div>
        <div className="mt-5 flex items-center justify-between">
          <span className="text-[11.5px] font-semibold" style={{ color }}>Approfondisci</span>
          <span className="h-9 w-9 rounded-full flex items-center justify-center text-white"
                style={{ background: color, boxShadow: `0 8px 18px -6px ${color}66` }}>
            <ArrowRight className="h-4 w-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}


function FeatureSlide({ to, onClick, testid, eyebrow, title, body, cta, ctaIcon: CtaIcon, photo, gradient, accent }) {
  const Container = onClick ? "button" : Link;
  const containerProps = onClick
    ? { type: "button", onClick: () => { hapticTap(); onClick(); } }
    : { to, onClick: hapticTap };
  return (
    <Container
      {...containerProps}
      data-testid={testid}
      className="block relative rounded-3xl overflow-hidden border border-[#F0EBE3] active:scale-[0.985] transition-transform text-left bg-white"
      style={{
        boxShadow: "0 10px 28px -12px rgba(0,0,0,0.10), 0 2px 6px -2px rgba(0,0,0,0.04)",
        width: "min(72vw, 260px)",
        minHeight: "420px",
      }}
    >
      {/* Top: illustration edge-to-edge (Trade Republic style — vertical portrait) */}
      {photo && (
        <div
          className="relative w-full h-[260px] overflow-hidden"
          style={{
            background: gradient || "linear-gradient(135deg, #FFF4E5 0%, #FFE6CC 100%)",
          }}
        >
          <IlloImg slug={photo} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
          {/* Small floating eyebrow tag on the image */}
          <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur text-[9.5px] uppercase tracking-[0.18em] font-bold"
               style={{ color: accent, boxShadow: "0 4px 12px -4px rgba(0,0,0,0.15)" }}>
            <span className="h-1 w-1 rounded-full" style={{ background: accent }} />
            {eyebrow}
          </div>
        </div>
      )}
      {/* Bottom: clean text panel + CTA arrow (Trade Republic minimal) */}
      <div className="relative p-4 sm:p-5">
        <h3 className="font-display font-bold text-[18px] leading-tight text-[#0F0F0F] tracking-tight">
          {title}
        </h3>
        <p className="mt-1.5 text-[12.5px] text-[#6B6B6B] leading-snug line-clamp-2">{body}</p>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[12px] font-semibold" style={{ color: accent }}>
            {cta}
          </span>
          <span
            className="h-8 w-8 rounded-full flex items-center justify-center text-white"
            style={{
              background: accent,
              boxShadow: `0 6px 14px -4px ${accent}66`,
            }}
          >
            {CtaIcon ? <CtaIcon className="h-3.5 w-3.5" /> : <ArrowRight className="h-3.5 w-3.5" />}
          </span>
        </div>
      </div>
    </Container>
  );
}

function WeatherChip({ weather }) {
  if (!weather || typeof weather.temperature !== "number") {
    return (
      <div className="bg-white border border-[#F0EBE3] rounded-full px-3.5 py-2 flex items-center gap-2 text-sm shrink-0 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.06)]">
        <Sun className="h-4 w-4 text-[#F4B83C]" />
        <span className="text-[#1A1A1A] font-medium">—</span>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#F0EBE3] rounded-full px-3.5 py-2 flex items-center gap-2 text-sm shrink-0 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.06)]">
      <Sun className="h-4 w-4 text-[#F4B83C]" />
      <span className="text-[#1A1A1A] font-semibold">{Math.round(weather.temperature)}°</span>
    </div>
  );
}

function ago(ts) {
  if (!ts) return "ora";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60) return "ora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  return `${Math.floor(diff / 86400)} g fa`;
}
